import { AllStoreSettings } from "@/constants/stores";
import {
  ApiPaginationInfo,
  IdNamePair,
  PaginatedCardsSettings,
  PaginatedCardsStore,
} from "@/types/global";
import { defineStore } from "pinia";

function getInitialPaginatedCardsStore(): PaginatedCardsStore {
  const paginatedCardsStore: PaginatedCardsStore = {
    AllCardsData: {},
    FilteredCardsData: {},
    AllFilterFieldOptions: {},
    SelectedFilters: [],
    CurrentSearchText: null,
    CurrentCategoryRadioId: null,
    CurrentPageNumber: 1,
    CardsDownloadData: [],
    ApiPaginationState: {},
    AllowApiCall: {},
    DataLoading: false,
  };
  return JSON.parse(JSON.stringify(paginatedCardsStore));
}

export const createPaginatedCardsStore = (storeId: string) => {
  const storeKey = storeId as keyof typeof AllStoreSettings;
  const storeSettings = AllStoreSettings[storeKey] as PaginatedCardsSettings;
  return defineStore(storeId, {
    state: (): PaginatedCardsStore => getInitialPaginatedCardsStore(),
    getters: {
      StoreSettings(): PaginatedCardsSettings {
        return storeSettings;
      },
      CurrentCategoryData(): Array<any> | null {
        if (this.CurrentCategoryRadioId === null) {
          return null;
        }
        return this.AllCardsData[this.CurrentCategoryRadioId];
      },
      CurrentFilteredData(): Array<any> | null {
        if (this.CurrentCategoryRadioId === null) {
          return null;
        }
        return this.FilteredCardsData[this.CurrentCategoryRadioId];
      },
      CurrentPageCards(): Array<any> | null {
        if (!this.CurrentFilteredData) {
          return null;
        }
        const startIndex = (this.CurrentPageNumber - 1) * storeSettings.UiPageSize;
        const endIndex = startIndex + storeSettings.UiPageSize;
        return this.CurrentFilteredData.slice(startIndex, endIndex);
      },
      CurrentFiltersOptions(): Array<Array<IdNamePair>> | null {
        if (this.CurrentCategoryRadioId === null) {
          return null;
        }
        return this.AllFilterFieldOptions[this.CurrentCategoryRadioId];
      },
      ValidFilterIndexes(): Array<number> | null {
        if (this.CurrentCategoryRadioId === null) {
          return null;
        }
        return storeSettings.FilterFields.map((_, index) => index).filter((index) => {
          if (this.CurrentCategoryRadioId === null) {
            return false;
          }
          const options = this.AllFilterFieldOptions[this.CurrentCategoryRadioId];
          return options && options[index] && options[index].length > 0;
        });
      },
      CurrentApiPaginationInfo(): ApiPaginationInfo | null {
        if (this.CurrentCategoryRadioId === null) {
          return null;
        }
        return this.ApiPaginationState[this.CurrentCategoryRadioId];
      },
      ShowAvailableCount(): boolean {
        if (this.CurrentApiPaginationInfo === null) {
          return false;
        }
        return this.CurrentApiPaginationInfo.totalPages >= 1;
      },
      DisableGetMoreRecords(): boolean {
        if (this.CurrentApiPaginationInfo === null) {
          return false;
        }
        return (
          this.CurrentApiPaginationInfo.totalPages === this.CurrentApiPaginationInfo.pageNumber
        );
      },
    },
    actions: {
      initializeApiPagination() {
        if (this.CurrentCategoryRadioId === null) {
          return;
        }
        this.ApiPaginationState[this.CurrentCategoryRadioId] = {
          pageNumber: 1,
          fetchedCount: 0,
          availableCount: 0,
          totalPages: 0,
        };
      },
      updateCardsList(cardsList: Array<unknown>, selectedCategoryId: number) {
        if (!this.AllCardsData[selectedCategoryId]) {
          this.AllCardsData[selectedCategoryId] = [];
        }
        this.AllCardsData[selectedCategoryId].push(...cardsList);
        this.updateFilterOptions(selectedCategoryId);
        this.updateFilteredCards(selectedCategoryId);
      },
      async updateFilterOptions(selectedCategoryId: number) {
        this.AllFilterFieldOptions[selectedCategoryId] = storeSettings.FilterFields.map(() => []);
        const foundIds = storeSettings.FilterFields.map(() => new Set<number>());
        const currentFilteredCardsData = this.AllCardsData[selectedCategoryId];
        if (currentFilteredCardsData === null) {
          return;
        }
        currentFilteredCardsData.forEach((card) => {
          storeSettings.FilterFields.forEach((field, index) => {
            const fieldData = card[field];
            if (Array.isArray(fieldData)) {
              fieldData.forEach((item) =>
                this.addFieldToOptions(index, item, foundIds[index], selectedCategoryId),
              );
            } else {
              this.addFieldToOptions(index, fieldData, foundIds[index], selectedCategoryId);
            }
          });
        });
        this.AllFilterFieldOptions[selectedCategoryId].forEach((options) =>
          options?.sort((a, b) => a.Name.localeCompare(b.Name)),
        );
      },
      addFieldToOptions(
        index: number,
        fieldData: IdNamePair,
        foundIds: Set<number>,
        selectedCategoryId: number,
      ) {
        if (!fieldData || foundIds.has(fieldData.Id)) {
          return;
        }
        foundIds.add(fieldData.Id);
        this.AllFilterFieldOptions[selectedCategoryId][index].push(fieldData);
      },
      updateFilteredCards(selectedCategoryId: number) {
        const currentFilteredCardsData = this.AllCardsData[selectedCategoryId];
        if (currentFilteredCardsData === null) {
          return;
        }
        this.FilteredCardsData[selectedCategoryId] = currentFilteredCardsData.filter((card) => {
          if (this.SelectedFilters) {
            for (let f = 0; f < storeSettings.FilterFields.length; f++) {
              const selectedField = this.SelectedFilters[f];
              if (!selectedField) {
                continue;
              }
              const cardData = card[storeSettings.FilterFields[f]];
              if (!cardData) {
                return false;
              }
              if (Array.isArray(cardData)) {
                const matchFound = cardData.some((item) => item?.Id === selectedField);
                if (!matchFound) return false;
              } else if (cardData.Id !== selectedField) {
                return false;
              }
            }
          }
          if (storeSettings.SearchFields && this.CurrentSearchText) {
            const searchText = this.CurrentSearchText.toLowerCase();
            return storeSettings.SearchFields.some((field) => {
              const fieldData = card[field];
              if (!fieldData) {
                return false;
              }
              const searchString =
                typeof fieldData === "object" && fieldData.Name
                  ? fieldData.Name
                  : String(fieldData);
              return searchString.toLowerCase().includes(searchText);
            });
          }
          return true;
        });
        this.CardsDownloadData = Object.values(this.FilteredCardsData[selectedCategoryId]);
      },
      onCategoryRadioSelect(radioId: number) {
        this.CurrentCategoryRadioId = radioId;
        this.CurrentPageNumber = 1;
        if (this.CurrentFilteredData) {
          this.updateFilteredCards(radioId);
        }
      },
      onFilterDropdownChange(filterIndex: number, selectedId: number) {
        if (this.CurrentCategoryRadioId === null) {
          return;
        }
        this.SelectedFilters[filterIndex] = selectedId;
        this.updateFilteredCards(this.CurrentCategoryRadioId);
        this.CurrentPageNumber = 1;
      },
      onFilterSearchChange(searchedValue: string) {
        if (this.CurrentCategoryRadioId === null) {
          return;
        }
        this.CurrentSearchText = searchedValue;
        this.updateFilteredCards(this.CurrentCategoryRadioId);
        this.CurrentPageNumber = 1;
      },
      onPageChange(currentPage: number) {
        this.CurrentPageNumber = currentPage;
      },
      setStateForGetMoreRecords() {
        if (this.CurrentCategoryRadioId === null) {
          return;
        }
        if (this.CurrentApiPaginationInfo) {
          this.CurrentApiPaginationInfo.pageNumber += 1;
          this.AllowApiCall[this.CurrentCategoryRadioId] = true;
        }
      },
      resetCategoryData(categoryIds: Array<number> | null) {
        if (this.CurrentCategoryRadioId === null) {
          return;
        }
        // This case is used where category radio buttons are not used
        if (categoryIds === null) {
          categoryIds = [0];
        }
        const currentCategoryId = this.CurrentCategoryRadioId;
        categoryIds.forEach((categoryId) => {
          const resetCategoryId = categoryId === 0 ? currentCategoryId : categoryId;
          this.AllCardsData[resetCategoryId] = null;
          this.FilteredCardsData[resetCategoryId] = null;
          this.ApiPaginationState[resetCategoryId] = null;
          this.AllowApiCall[resetCategoryId] = true;
        });
      },
      resetData() {
        this.AllCardsData = {};
        this.FilteredCardsData = {};
        this.AllowApiCall = {};
        this.CurrentPageNumber = 1;
        this.SelectedFilters = [];
        this.CurrentSearchText = null;
      },
    },
  });
};
